import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Chip,
  useMediaQuery,
  useTheme,
  Fade,
  IconButton,
  Card,
  CardContent,
  Avatar,
  Rating,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  ArrowForward as ArrowIcon,
  TrendingUp as TrendingIcon,
  Translate as TranslateIcon,
  VolumeUp as VolumeIcon,
  Analytics as AnalyticsIcon,
  Handshake as HandshakeIcon,
  Language as LanguageIcon,
  CheckCircle as CheckIcon,
  AutoAwesome as SparkleIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Groups as GroupsIcon,
  Star as StarIcon,
  FormatQuote as QuoteIcon,
  Upload as UploadIcon,
  Memory as MemoryIcon,
  Business as BusinessIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import wrk from '../assests/wall23.jpg';
import logoImg from '../assests/logo.jpg';
// Import partner logos
import momulimisaLogo from '../assests/momulimisa.png';
import bodaaiLogo from '../assests/bodaai.png';
import quakeLogo from '../assests/quake.png';
import ctdruLogo from '../assests/ctdru.png';

// ── Animated count-up component ──────────────────────────────────────────────
const AnimatedStat = ({ numeric, suffix, label, color, icon }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.6 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let current = 0;
    const duration = 1400;
    const steps = 60;
    const increment = numeric / steps;
    const interval = duration / steps;
    const timer = setInterval(() => {
      current += increment;
      if (current >= numeric) { setCount(numeric); clearInterval(timer); }
      else setCount(Math.floor(current));
    }, interval);
    return () => clearInterval(timer);
  }, [started, numeric]);

  return (
    <Box ref={ref} sx={{ textAlign: 'center', py: { xs: 3, md: 2 }, px: 2 }}>
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 42, height: 42, borderRadius: '12px',
        backgroundColor: `${color}18`, border: `1px solid ${color}30`, color, mb: 1.5, mx: 'auto',
      }}>
        {icon}
      </Box>
      <Typography sx={{
        fontWeight: 900, fontSize: { xs: '2.4rem', md: '3rem' }, lineHeight: 1,
        mb: 0.5, color: 'white', fontFamily: "'Plus Jakarta Sans', sans-serif",
        letterSpacing: '-0.03em',
      }}>
        {count}{suffix}
      </Typography>
      <Typography sx={{ color: '#9CA3AF', fontSize: { xs: '0.8rem', md: '0.9rem' }, fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const Welcome = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);



  const features = [
    {
      icon: <TranslateIcon />,
      title: 'Language & Communication AI',
      description: 'Advanced machine translation, transcription, and multilingual communication products for African languages and developing markets',
      color: '#F59E0B'
    },
    {
      icon: <VolumeIcon />,
      title: 'Voice & Business AI',
      description: 'AI-powered voice assistants, chatbots, and business automation products for hotels, agriculture, and various industries',
      color: '#F59E0B'
    },
    {
      icon: <AnalyticsIcon />,
      title: 'Analytics & Data AI',
      description: 'Intelligent analytics platforms, OCR solutions, and data processing products for business insights and automation',
      color: '#F59E0B'
    }
  ];

  const partners = [
    {
      name: "M-Omulimisa",
      description: "AI-powered agricultural extension services and mobile technology solutions for smallholder farmers",
      website: "https://m-omulimisa.com",
      logo: momulimisaLogo,
      color: '#F59E0B',
      category: "Agriculture"
    },
    {
      name: "Boda AI Factory",
      description: "AI and Data Science solutions for African organizations across agriculture, healthcare, and finance",
      website: "http://www.boda-ai-factory.com",
      logo: bodaaiLogo,
      color: '#F59E0B',
      category: "AI & Data Science"
    },
    {
      name: "Quake",
      description: "Creative advertising and brand solutions with innovative problem-solving approach",
      website: "https://quakebrandfix.com",
      logo: quakeLogo,
      color: '#F59E0B',
      category: "Creative & Advertising"
    },
    {
      name: "CTDRU",
      description: "Uganda's leading virtual technology hub for consumer protection and digital rights",
      website: "https://ctdrug.org",
      logo: ctdruLogo,
      color: '#F59E0B',
      category: "Technology & Rights"
    }
  ];

  const stats = [
    { numeric: 10, suffix: '+', label: 'African Languages', color: '#F59E0B', icon: <LanguageIcon /> },
    { numeric: 7,  suffix: '',  label: 'AI Products',        color: '#F59E0B', icon: <SparkleIcon /> },
    { numeric: 95, suffix: '%', label: 'Translation Accuracy', color: '#F59E0B', icon: <CheckIcon /> },
    { numeric: 4,  suffix: '',  label: 'Strategic Partners', color: '#F59E0B', icon: <HandshakeIcon /> },
  ];

  const howItWorks = [
    {
      step: '01',
      icon: <UploadIcon sx={{ fontSize: 32 }} />,
      title: 'Submit Your Content',
      description: 'Upload audio, video, documents, or type text in any language. Our system accepts 20+ formats including MP3, MP4, PDF, DOCX, and plain text.',
      color: '#F59E0B',
    },
    {
      step: '02',
      icon: <MemoryIcon sx={{ fontSize: 32 }} />,
      title: 'AI Processing Engine',
      description: 'Our multilingual AI models — trained on 10+ African languages — transcribe, translate, and contextualize your content while preserving cultural nuance.',
      color: '#F59E0B',
    },
    {
      step: '03',
      icon: <DownloadIcon sx={{ fontSize: 32 }} />,
      title: 'Receive Accurate Results',
      description: 'Get polished translations, transcriptions, summaries, or voice outputs in seconds. Export to your preferred format or integrate via our REST API.',
      color: '#F59E0B',
    },
  ];

  const testimonials = [
    {
      quote: "PHOSAI's Avoices platform transformed how we communicate with smallholder farmers across Uganda. Getting agricultural advice in Luganda has increased farmer adoption by over 40%.",
      name: 'M-Omulimisa Team',
      role: 'Agricultural Technology Partner',
      org: 'M-Omulimisa',
      logo: momulimisaLogo,
      rating: 5,
      color: '#F59E0B',
    },
    {
      quote: "The PhosAI Agents platform let us deploy a Swahili-speaking customer support bot in under a week. The voice-to-voice capability is genuinely impressive — our clients felt heard for the first time.",
      name: 'Boda AI Factory',
      role: 'AI & Data Science Solutions',
      org: 'Boda AI Factory',
      logo: bodaaiLogo,
      rating: 5,
      color: '#F59E0B',
    },
    {
      quote: "CTDRU leverages PHOSAI's translation capabilities to make digital rights content accessible across East Africa. Language should never be a barrier to understanding your rights online.",
      name: 'CTDRU',
      role: 'Consumer Technology & Digital Rights',
      org: 'CTDRU Uganda',
      logo: ctdruLogo,
      rating: 5,
      color: '#F59E0B',
    },
  ];

  const whyPhosai = [
    {
      icon: <TranslateIcon sx={{ fontSize: 28 }} />,
      title: 'African-First Design',
      description: "Built from the ground up for African languages, accents, and contexts — not adapted from Western models that struggle with tonal and Bantu languages.",
      color: '#F59E0B',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 28 }} />,
      title: 'Real-Time Processing',
      description: "Sub-second response times for voice translation and live transcription. Designed for field use across low-bandwidth environments in rural Africa.",
      color: '#F59E0B',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 28 }} />,
      title: 'Privacy & Data Sovereignty',
      description: "Your data stays in Africa. We comply with African data protection frameworks and offer on-premise deployment for sensitive government or healthcare data.",
      color: '#F59E0B',
    },
    {
      icon: <GroupsIcon sx={{ fontSize: 28 }} />,
      title: 'Academic Excellence',
      description: "Backed by research at Makerere University — one of Africa's top AI research institutions. Our models are peer-validated and continuously improving.",
      color: '#F59E0B',
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 28 }} />,
      title: 'Measurable ROI',
      description: "Clients report 60% reduction in translation costs, 80% faster document processing, and 300% improvement in multilingual customer engagement.",
      color: '#F59E0B',
    },
    {
      icon: <HandshakeIcon sx={{ fontSize: 28 }} />,
      title: 'End-to-End Support',
      description: "From integration to fine-tuning on your domain-specific vocabulary, our team provides hands-on support to ensure your AI deployment succeeds.",
      color: '#F59E0B',
    },
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
      }}>
        {/* Clean Corporate Grid Background */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.4,
          backgroundImage: `radial-gradient(#9CA3AF 1px, transparent 1px)`,
          backgroundSize: '24px 24px',
          zIndex: 0,
        }} />

        {/* Transparent Logo Background Left */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '-10%',
          transform: 'translateY(-50%)',
          height: '90vh',
          width: '50vw',
          backgroundImage: `url(${logoImg})`,
          backgroundSize: 'contain',
          backgroundPosition: 'left center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.07,
          zIndex: 1,
          mixBlendMode: 'multiply'
        }} />

        {/* Transparent Logo Background Right */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          right: '-10%',
          transform: 'translateY(-50%)',
          height: '90vh',
          width: '50vw',
          backgroundImage: `url(${logoImg})`,
          backgroundSize: 'contain',
          backgroundPosition: 'right center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.07,
          zIndex: 1,
          mixBlendMode: 'multiply'
        }} />

        {/* Premium Blur Glows */}
        <Box sx={{
          position: 'absolute',
          top: '-20%',
          left: '10%',
          width: '50vw',
          height: '50vw',
          background: 'radial-gradient(circle, rgba(245,158,11, 0.04) 0%, transparent 60%)',
          filter: 'blur(80px)',
          zIndex: 0,
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '-20%',
          right: '10%',
          width: '50vw',
          height: '50vw',
          background: 'radial-gradient(circle, rgba(245,158,11, 0.04) 0%, transparent 60%)',
          filter: 'blur(80px)',
          zIndex: 0,
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={6} alignItems="center" justifyContent="center" sx={{ minHeight: '100vh' }}>
            {/* Center Content */}
            <Grid item xs={12} md={10} lg={8}>
              <Fade in={isVisible} timeout={1200}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>

                  {/* Main Title */}
                  <Typography
                    variant="h1"
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: '3.5rem', sm: '4.5rem', md: '5.5rem', lg: '6.5rem' },
                      lineHeight: { xs: 1.05, md: 0.95 },
                      letterSpacing: '-0.05em',
                      mb: { xs: 2, md: 3 },
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    <Box component="span" sx={{ color: '#111827' }}>PHOSAI</Box>
                  </Typography>

                  {/* Subtitle */}
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                      lineHeight: 1.3,
                      mb: { xs: 3, md: 4 },
                      maxWidth: 700,
                      textAlign: 'center',
                      mx: 'auto',
                      letterSpacing: '-0.03em',
                      color: '#374151'
                    }}
                  >
                    World-Class AI for <br />
                    <Box component="span" sx={{
                      background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}>
                      Developing Countries
                    </Box>
                  </Typography>

                  {/* Description */}
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: { xs: '1.1rem', md: '1.25rem' },
                      lineHeight: 1.7,
                      color: '#4B5563',
                      mb: { xs: 5, md: 6 },
                      maxWidth: 700,
                      fontWeight: 400,
                      textAlign: 'center',
                      mx: 'auto',
                    }}
                  >
                    We specialize in solving complex challenges in communication, agriculture, finance, 
                    education, ethics, and analytics. Our products bridge technological gaps and empower communities to thrive in the digital economy.
                  </Typography>

                  {/* CTA Buttons */}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 2, md: 3 }, 
                    flexWrap: 'wrap', 
                    justifyContent: 'center'
                  }}>
                    <Button
                      variant="contained"
                      size="large"
                      component={Link}
                      to="/products"
                      endIcon={<ArrowIcon />}
                      sx={{
                        background: '#F59E0B',
                        color: '#ffffff',
                        px: { xs: 4, md: 5 },
                        py: { xs: 1.5, md: 2 },
                        borderRadius: '50px',
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: '0 8px 20px -4px rgba(245,158,11, 0.4)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          boxShadow: '0 12px 25px -4px rgba(245,158,11, 0.5)',
                          background: '#D97706',
                        }
                      }}
                    >
                      Discover AI Products
                    </Button>

                    <Button
                      variant="outlined"
                      size="large"
                      component={Link}
                      to="/aboutus"
                      startIcon={<PlayIcon />}
                      sx={{
                        color: '#374151',
                        border: '1px solid #E5E7EB',
                        backgroundColor: '#ffffff',
                        px: { xs: 4, md: 5 },
                        py: { xs: 1.5, md: 2 },
                        borderRadius: '50px',
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          borderColor: '#D1D5DB',
                          backgroundColor: '#F9FAFB',
                          transform: 'translateY(-3px)',
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)',
                        }
                      }}
                    >
                      Learn More
                    </Button>
                  </Box>

                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Impact Stats Section */}
      <Box sx={{
        py: { xs: 5, md: 6 },
        background: 'linear-gradient(135deg, #0D0D1A 0%, #111827 100%)',
        borderTop: '1px solid rgba(245,158,11, 0.15)',
        borderBottom: '1px solid rgba(245,158,11, 0.15)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Background grid pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.03,
          backgroundImage: `
            linear-gradient(rgba(245,158,11, 1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(245,158,11, 1) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }} />
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 2, md: 0 }}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index} sx={{
                borderRight: { md: index < 3 ? '1px solid rgba(245,158,11, 0.12)' : 'none' },
              }}>
                <AnimatedStat
                  numeric={stat.numeric}
                  suffix={stat.suffix}
                  label={stat.label}
                  color={stat.color}
                  icon={stat.icon}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Products Section */}
      <Box sx={{
        py: { xs: 6, md: 12 },
        background: 'linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Container maxWidth="xl">
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Chip
              label="CORE CAPABILITIES"
              sx={{
                backgroundColor: 'rgba(245,158,11, 0.1)',
                color: '#F59E0B',
                fontWeight: 700,
                fontSize: '0.75rem',
                mb: 2.5,
                px: 2.5,
                py: 1,
                borderRadius: '100px',
                letterSpacing: '0.1em',
                border: '1px solid rgba(245,158,11, 0.2)',
              }}
            />
            <Typography variant="h2" sx={{
              fontWeight: 900,
              fontSize: { xs: '2rem', md: '3rem' },
              color: '#111827',
              mb: 2,
              letterSpacing: '-0.03em',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}>
              Built for Africa's Future
            </Typography>
            <Typography variant="body1" sx={{
              color: '#6B7280',
              fontSize: { xs: '1rem', md: '1.15rem' },
              maxWidth: 580,
              mx: 'auto',
              lineHeight: 1.7,
            }}>
              Three pillars of AI innovation powering transformation across the continent
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Fade in={isVisible} timeout={800 + index * 200}>
                  <Box sx={{
                    textAlign: 'center',
                    p: { xs: 3, md: 4 },
                    borderRadius: '24px',
                    background: 'white',
                    border: '1px solid rgba(245,158,11, 0.1)',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(245,158,11, 0.15)',
                      borderColor: `${feature.color}30`,
                    }
                  }}>
                    <Box sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: { xs: 60, md: 80 },
                      height: { xs: 60, md: 80 },
                      borderRadius: '50%',
                      backgroundColor: `${feature.color}15`,
                      color: feature.color,
                      mb: { xs: 2, md: 3 },
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.1) rotate(5deg)',
                        backgroundColor: `${feature.color}25`,
                      }
                    }}>
                      <Box sx={{ fontSize: { xs: 28, md: 36 }, color: feature.color }}>
                        {feature.icon}
                      </Box>
                    </Box>
                    
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 800,
                        color: '#111827',
                        mb: 2,
                        fontSize: '1.25rem',
                      }}
                    >
                      {feature.title}
                    </Typography>
                    
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#6B7280',
                        lineHeight: 1.7,
                        fontSize: '1rem',
                        fontWeight: 500,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Partners Section */}
      <Box sx={{
        py: { xs: 6, md: 12 },
        background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Fade in={isVisible} timeout={800}>
              <Box>
                <Chip
                  icon={<HandshakeIcon />}
                  label="STRATEGIC PARTNERSHIPS"
                  sx={{
                    backgroundColor: 'rgba(245,158,11, 0.1)',
                    color: '#F59E0B',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    mb: { xs: 2, md: 3 },
                    px: { xs: 2, md: 3 },
                    py: { xs: 1, md: 1.5 },
                    borderRadius: '20px',
                    '& .MuiChip-icon': { color: '#F59E0B' }
                  }}
                />
                
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 900,
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '3.5rem' },
                    lineHeight: 1.1,
                    letterSpacing: '-0.02em',
                    mb: { xs: 3, md: 4 },
                    color: '#111827',
                  }}
                >
                  Our Partners
                </Typography>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: { xs: '1rem', md: '1.2rem' },
                    lineHeight: 1.7,
                    color: '#6B7280',
                    maxWidth: 700,
                    margin: '0 auto',
                    fontWeight: 500,
                    px: { xs: 2, md: 0 },
                  }}
                >
                  Collaborating with leading organizations to deliver innovative AI solutions across diverse sectors
                </Typography>
              </Box>
            </Fade>
          </Box>

          <Grid container spacing={{ xs: 3, md: 3 }}>
            {partners.map((partner, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Fade in={isVisible} timeout={800 + index * 150}>
                  <Box sx={{
                    textAlign: 'center',
                    p: { xs: 3, md: 4 },
                    borderRadius: '24px',
                    background: 'white',
                    border: '1px solid rgba(245,158,11, 0.1)',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s ease',
                    height: '100%',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(245,158,11, 0.15)',
                      borderColor: `${partner.color}30`,
                    }
                  }}
                  onClick={() => window.open(partner.website, '_blank')}
                  >
                    <Box sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: { xs: 80, md: 100 },
                      height: { xs: 80, md: 100 },
                      borderRadius: '16px',
                      backgroundColor: 'white',
                      border: `2px solid ${partner.color}20`,
                      mb: { xs: 2, md: 3 },
                      transition: 'all 0.3s ease',
                      padding: 2,
                      '&:hover': {
                        transform: 'scale(1.05)',
                        borderColor: partner.color,
                        boxShadow: `0 8px 25px ${partner.color}30`,
                      }
                    }}>
                      <img 
                        src={partner.logo} 
                        alt={partner.name} 
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'contain',
                          filter: 'grayscale(0%)',
                          transition: 'all 0.3s ease'
                        }} 
                      />
                    </Box>
                    
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 800,
                        color: '#111827',
                        mb: 1,
                        fontSize: '1.25rem',
                      }}
                    >
                      {partner.name}
                    </Typography>

                    <Chip
                      label={partner.category}
                      size="small"
                      sx={{
                        backgroundColor: `${partner.color}15`,
                        color: partner.color,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        mb: 2,
                        height: 24,
                      }}
                    />
                    
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#6B7280',
                        lineHeight: 1.6,
                        fontSize: '0.9rem',
                        fontWeight: 500,
                      }}
                    >
                      {partner.description}
                    </Typography>

                    <Box sx={{
                      mt: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      color: partner.color,
                      fontWeight: 600,
                      fontSize: '0.85rem',
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Visit Website
                      </Typography>
                      <ArrowIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── HOW IT WORKS ───────────────────────────────────────────────────── */}
      <Box sx={{
        py: { xs: 8, md: 14 },
        background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Subtle grid bg */}
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, opacity: 0.025,
          backgroundImage: 'linear-gradient(rgba(245,158,11,1) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,1) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />
        <Container maxWidth="xl" sx={{ position: 'relative' }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
            <Chip label="HOW IT WORKS" sx={{
              backgroundColor: 'rgba(245,158,11, 0.1)', color: '#F59E0B',
              fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.12em',
              mb: 2.5, px: 2.5, py: 1, borderRadius: '100px',
              border: '1px solid rgba(245,158,11, 0.2)',
            }} />
            <Typography variant="h2" sx={{
              fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, color: '#111827',
              mb: 2, letterSpacing: '-0.03em', fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}>
              From Input to Insight in Seconds
            </Typography>
            <Typography sx={{
              color: '#6B7280', fontSize: { xs: '1rem', md: '1.15rem' }, maxWidth: 560, mx: 'auto', lineHeight: 1.7,
            }}>
              Our AI pipeline handles every step — so you focus on the outcome, not the process
            </Typography>
          </Box>

          {/* Steps */}
          <Grid container spacing={{ xs: 3, md: 2 }} alignItems="stretch">
            {howItWorks.map((step, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade in={isVisible} timeout={800 + index * 200}>
                  <Box sx={{ position: 'relative', height: '100%' }}>
                    {/* Connector arrow between steps (desktop only) */}
                    {index < 2 && (
                      <Box sx={{
                        display: { xs: 'none', md: 'flex' },
                        position: 'absolute', right: -24, top: '50%',
                        transform: 'translateY(-50%)', zIndex: 10,
                        width: 48, height: 48,
                        alignItems: 'center', justifyContent: 'center',
                        background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                        borderRadius: '50%',
                        boxShadow: '0 8px 24px rgba(245,158,11,0.4)',
                        color: 'white',
                      }}>
                        <ArrowIcon sx={{ fontSize: 20 }} />
                      </Box>
                    )}
                    <Box sx={{
                      p: { xs: 4, md: 5 }, borderRadius: '24px', height: '100%',
                      background: 'white',
                      border: `2px solid ${step.color}20`,
                      boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
                      transition: 'all 0.3s ease',
                      position: 'relative', overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: `0 20px 50px ${step.color}20`,
                        border: `2px solid ${step.color}50`,
                      },
                    }}>
                      {/* Step number watermark */}
                      <Typography sx={{
                        position: 'absolute', top: 16, right: 24,
                        fontSize: '5rem', fontWeight: 900, lineHeight: 1,
                        color: `${step.color}08`,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        userSelect: 'none',
                      }}>
                        {step.step}
                      </Typography>

                      {/* Icon */}
                      <Box sx={{
                        width: 64, height: 64, borderRadius: '18px',
                        background: `linear-gradient(135deg, ${step.color}20, ${step.color}10)`,
                        border: `1px solid ${step.color}30`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: step.color, mb: 3,
                      }}>
                        {step.icon}
                      </Box>

                      <Chip label={`Step ${step.step}`} size="small" sx={{
                        height: 22, fontSize: '0.68rem', fontWeight: 700,
                        backgroundColor: `${step.color}15`, color: step.color,
                        border: `1px solid ${step.color}25`, mb: 2,
                        letterSpacing: '0.05em',
                      }} />

                      <Typography variant="h5" sx={{
                        fontWeight: 800, color: '#111827', mb: 2,
                        fontSize: '1.2rem', fontFamily: "'Plus Jakarta Sans', sans-serif",
                      }}>
                        {step.title}
                      </Typography>
                      <Typography sx={{
                        color: '#6B7280', lineHeight: 1.75, fontSize: '0.95rem', fontWeight: 500,
                      }}>
                        {step.description}
                      </Typography>
                    </Box>
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>

          {/* CTA below */}
          <Box sx={{ textAlign: 'center', mt: { xs: 6, md: 8 } }}>
            <Button
              component={Link} to="/products" variant="contained" size="large"
              endIcon={<ArrowIcon />}
              sx={{
                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                color: 'white', px: 5, py: 1.75, borderRadius: '14px',
                fontWeight: 700, fontSize: '1rem', textTransform: 'none',
                boxShadow: '0 12px 35px rgba(245,158,11,0.35)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #D97706 0%, #6D28D9 100%)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 20px 50px rgba(245,158,11,0.45)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Explore All Products
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── WHY CHOOSE PHOSAI ──────────────────────────────────────────────── */}
      <Box sx={{
        py: { xs: 8, md: 14 },
        background: 'linear-gradient(135deg, #0D0D1A 0%, #111827 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Radial glow */}
        <Box sx={{
          position: 'absolute', top: '30%', left: '10%', width: 500, height: 500,
          borderRadius: '50%', pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(245,158,11,0.08) 0%, transparent 70%)',
        }} />
        <Container maxWidth="xl" sx={{ position: 'relative' }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
            <Chip label="WHY PHOSAI" sx={{
              backgroundColor: 'rgba(245,158,11,0.15)', color: '#A78BFA',
              fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.12em',
              mb: 2.5, px: 2.5, py: 1, borderRadius: '100px',
              border: '1px solid rgba(245,158,11,0.25)',
            }} />
            <Typography variant="h2" sx={{
              fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' },
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FCD34D 100%)',
              backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              mb: 2, letterSpacing: '-0.03em', fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}>
              Built Differently. Designed for Africa.
            </Typography>
            <Typography sx={{
              color: '#9CA3AF', fontSize: { xs: '1rem', md: '1.1rem' }, maxWidth: 580, mx: 'auto', lineHeight: 1.7,
            }}>
              Generic AI tools fail at African languages. PHOSAI is purpose-built — from training data to deployment infrastructure.
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 3, md: 3 }}>
            {whyPhosai.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Fade in={isVisible} timeout={800 + index * 120}>
                  <Box sx={{
                    p: { xs: 3.5, md: 4 }, borderRadius: '20px', height: '100%',
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.07)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: `rgba(${item.color === '#F59E0B' ? '245,158,11' : item.color === '#F59E0B' ? '245,158,11' : item.color === '#F59E0B' ? '245,158,11' : item.color === '#F59E0B' ? '245,158,11' : '245,158,11'},0.07)`,
                      border: `1px solid ${item.color}30`,
                      transform: 'translateY(-4px)',
                    },
                  }}>
                    <Box sx={{
                      width: 52, height: 52, borderRadius: '14px',
                      backgroundColor: `${item.color}18`, border: `1px solid ${item.color}30`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: item.color, mb: 2.5,
                    }}>
                      {item.icon}
                    </Box>
                    <Typography sx={{
                      fontWeight: 700, color: 'white', fontSize: '1.05rem', mb: 1.5,
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                    }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{
                      color: '#9CA3AF', fontSize: '0.9rem', lineHeight: 1.7, fontWeight: 500,
                    }}>
                      {item.description}
                    </Typography>
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── TESTIMONIALS ───────────────────────────────────────────────────── */}
      <Box sx={{
        py: { xs: 8, md: 14 },
        background: 'linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
            <Chip label="WHAT PARTNERS SAY" sx={{
              backgroundColor: 'rgba(245,158,11,0.1)', color: '#F59E0B',
              fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.12em',
              mb: 2.5, px: 2.5, py: 1, borderRadius: '100px',
              border: '1px solid rgba(245,158,11,0.2)',
            }} />
            <Typography variant="h2" sx={{
              fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, color: '#111827',
              mb: 2, letterSpacing: '-0.03em', fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}>
              Trusted by Africa's Innovators
            </Typography>
            <Typography sx={{
              color: '#6B7280', fontSize: { xs: '1rem', md: '1.1rem' },
              maxWidth: 540, mx: 'auto', lineHeight: 1.7,
            }}>
              Organizations across East Africa rely on PHOSAI to break language barriers and drive impact
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 3, md: 4 }}>
            {testimonials.map((t, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade in={isVisible} timeout={800 + index * 200}>
                  <Box sx={{
                    p: { xs: 4, md: 5 }, borderRadius: '24px', height: '100%',
                    background: 'white',
                    border: `1px solid ${t.color}20`,
                    boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
                    transition: 'all 0.3s ease', position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: `0 24px 60px ${t.color}18`,
                      border: `1px solid ${t.color}40`,
                    },
                    '&::before': {
                      content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
                      background: `linear-gradient(90deg, ${t.color}, ${t.color}60)`,
                      borderRadius: '24px 24px 0 0',
                    },
                  }}>
                    {/* Stars */}
                    <Box sx={{ display: 'flex', gap: 0.3, mb: 3 }}>
                      {[...Array(t.rating)].map((_, i) => (
                        <StarIcon key={i} sx={{ fontSize: 18, color: '#F59E0B' }} />
                      ))}
                    </Box>

                    {/* Quote icon */}
                    <QuoteIcon sx={{ fontSize: 36, color: `${t.color}30`, mb: 1.5, display: 'block' }} />

                    <Typography sx={{
                      color: '#374151', lineHeight: 1.75, fontSize: '0.95rem', fontWeight: 500,
                      mb: 4, fontStyle: 'italic',
                    }}>
                      "{t.quote}"
                    </Typography>

                    {/* Author */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 'auto' }}>
                      <Avatar src={t.logo} sx={{
                        width: 46, height: 46, borderRadius: '12px',
                        border: `2px solid ${t.color}30`,
                        backgroundColor: 'white',
                        '& img': { objectFit: 'contain', p: 0.5 },
                      }} />
                      <Box>
                        <Typography sx={{ fontWeight: 700, color: '#111827', fontSize: '0.9rem' }}>
                          {t.name}
                        </Typography>
                        <Typography sx={{ color: '#6B7280', fontSize: '0.78rem', fontWeight: 500 }}>
                          {t.role}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Fade in={isVisible} timeout={800}>
              <Box>
                <Chip
                  label="GET IN TOUCH"
                  sx={{
                    backgroundColor: 'rgba(245,158,11, 0.2)',
                    color: '#F59E0B',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    mb: 3,
                    px: 3,
                    py: 1.5,
                    borderRadius: '20px',
                  }}
                />
                
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 900,
                    fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                    lineHeight: 1.1,
                    letterSpacing: '-0.02em',
                    mb: 4,
                    color: 'white',
                  }}
                >
                  Ready to Transform Your Business?
                </Typography>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '1.2rem',
                    lineHeight: 1.7,
                    color: '#D1D5DB',
                    maxWidth: 700,
                    margin: '0 auto',
                    fontWeight: 500,
                    mb: 6,
                  }}
                >
                  Let's discuss how PHOSAI can help you leverage AI to solve real business challenges. 
                  Schedule a demo or get in touch with our team.
                </Typography>

                {/* Contact Methods */}
                <Grid container spacing={4} sx={{ maxWidth: 800, margin: '0 auto' }}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{
                      p: 3,
                      borderRadius: '20px',
                      background: 'rgba(245,158,11, 0.1)',
                      border: '1px solid rgba(245,158,11, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        background: 'rgba(245,158,11, 0.15)',
                      }
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700,
                          color: '#F59E0B',
                          mb: 2,
                        }}
                      >
                        Phone
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#D1D5DB',
                          mb: 2,
                        }}
                      >
                        +256 750 371 313
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#D1D5DB',
                        }}
                      >
                        +256 702 560 814
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Box sx={{
                      p: 3,
                      borderRadius: '20px',
                      background: 'rgba(245,158,11, 0.1)',
                      border: '1px solid rgba(245,158,11, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        background: 'rgba(245,158,11, 0.15)',
                      }
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700,
                          color: '#F59E0B',
                          mb: 2,
                        }}
                      >
                        Email
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#D1D5DB',
                          wordBreak: 'break-word',
                        }}
                      >
                        phosaico@gmail.com
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Box sx={{
                      p: 3,
                      borderRadius: '20px',
                      background: 'rgba(245,158,11, 0.1)',
                      border: '1px solid rgba(245,158,11, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        background: 'rgba(245,158,11, 0.15)',
                      }
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700,
                          color: '#F59E0B',
                          mb: 2,
                        }}
                      >
                        Location
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#D1D5DB',
                        }}
                      >
                        Makerere University
                        <br />
                        Kampala, Uganda
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* CTA Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: 3, 
                  flexWrap: 'wrap',
                  mt: 6 
                }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<ArrowIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                      color: 'white',
                      px: 5,
                      py: 2.5,
                      borderRadius: '20px',
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      boxShadow: '0 15px 35px rgba(245,158,11, 0.4)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 25px 50px rgba(245,158,11, 0.5)',
                        background: 'linear-gradient(135deg, #D97706 0%, #6D28D9 100%)',
                      }
                    }}
                    onClick={() => window.open('mailto:phosaico@gmail.com?subject=Schedule Demo - PHOSAI Products', '_blank')}
                  >
                    Schedule Demo
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      color: 'white',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      px: 5,
                      py: 2.5,
                      borderRadius: '20px',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 15px 35px rgba(255, 255, 255, 0.2)',
                      }
                    }}
                    onClick={() => window.open('tel:+256750371313', '_blank')}
                  >
                    Call Now
                  </Button>
                </Box>
              </Box>
            </Fade>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Welcome;